import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Link } from "@/lib/router-compat";
import { ChevronDown, Heart, LogIn, LogOut, MapPin, Menu, MessageCircle, Phone, RefreshCw, User, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useCustomerAuth } from "@/hooks/useCustomerAuth";
import { getSupabase } from "@/integrations/supabase/lazy";
import { hardRefresh } from "@/lib/hardRefresh";
import { STATIC_ASSETS, CARMAX_LOGO_SRCSET, CARMAX_LOGO_SIZES } from "@/lib/imageUtils";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import AnnouncementBanner from "@/components/layout/AnnouncementBanner";
import { useInteractionReady } from "@/components/ui/InteractionGate";

const NotificationDropdown = lazy(() => import("@/components/layout/NotificationDropdown"));

type MenuLink = { label: string; href: string; divider?: boolean };
type MenuGroup = { label: string; children: MenuLink[]; pill?: boolean };

// CarMax-style grouping: 4 primary links + a "More" pill. Every current page stays reachable.
const MENU: MenuGroup[] = [
  {
    label: "Shop",
    children: [
      { label: "All RVs", href: "/rv-search" },
      { label: "New RVs", href: "/rv-search?condition=new" },
      { label: "Used RVs", href: "/rv-search?condition=used" },
      { label: "RV Specials", href: "/rv-search?special=manager" },
      { label: "Shop by Brand", href: "/shop-by-brand" },
      { label: "Rentals", href: "/rentals" },
      { label: "Tow guides", href: "/tow-guides" },
    ],
  },
  {
    label: "Sell/Trade",
    children: [
      { label: "Sell your RV", href: "/sell-your-rv" },
      { label: "Trade-ins", href: "/contact-us-trade-ins" },
    ],
  },
  {
    label: "Finance",
    children: [
      { label: "RV financing", href: "/rv-financing" },
      { label: "Payment calculator", href: "/rv-financing" },
    ],
  },
  {
    label: "Research",
    children: [
      { label: "Information guides", href: "/guide" },
      { label: "Blog", href: "/blog" },
      { label: "Video gallery", href: "/video-gallery" },
      { label: "Why buy an RV?", href: "/why-buy-an-rv" },
      { label: "Tow guides", href: "/tow-guides" },
      { label: "Camping near us", href: "/camping-in-wilmington-north-carolina" },
      { label: "Community forum", href: "/forum" },
    ],
  },
  {
    label: "About Us",
    children: [
      { label: "About Us", href: "/about" },
      { label: "Testimonials", href: "/testimonials" },
      { label: "Route 66 RV Network", href: "/route-66-rv-network" },
    ],
  },
  {
    label: "More",
    pill: true,
    children: [
      { label: "Service & repairs", href: "/rv-service" },
      { label: "RV parts catalog", href: "/parts-catalog" },
      { label: "Component manuals", href: "/rv-manuals" },
      { label: "Pre-trip checklist", href: "/rv-pre-trip-check-list" },
      { label: "Roof seal guide", href: "/rv-how-to-check-your-roof-seals-1" },
      { label: "How to winterize", href: "/rv-how-to-winterize-my-rv" },
      { label: "Why Rex & Sons", href: "/about" },
      { label: "Testimonials", href: "/testimonials" },
      { label: "Route 66 RV Network", href: "/route-66-rv-network" },
      { label: "Camping club", href: "/camping-club" },
      { label: "Loyalty & rewards", href: "/loyalty-program" },
      { label: "FAQ & contact", href: "/contact" },
      { label: "Careers at Rex & Sons", href: "/application-career", divider: true },
    ],
  },
];

/** Clean white CarMax-style header used only by the CarMax test layout. */
export default function CarmaxHeader({ homepage = false }: { homepage?: boolean } = {}) {
  const { user, signOut } = useCustomerAuth();
  const interactionReady = useInteractionReady();
  const { data: isSuperAdmin } = useQuery({
    queryKey: ["is-super-admin", user?.id],
    queryFn: async () => {
      if (!user) return false;
      const supabase = await getSupabase();
      const { data } = await supabase.rpc("has_role", { _user_id: user.id, _role: "super_admin" });
      return !!data;
    },
    enabled: !!user && interactionReady,
    staleTime: 5 * 60_000,
  });
  const { data: headerSettings } = useQuery({
    queryKey: ["page-content", "global", "header"],
    queryFn: async () => {
      const supabase = await getSupabase();
      const { data } = await supabase.from("page_content").select("content").eq("page_slug", "global").eq("section_key", "header").maybeSingle();
      return (data?.content ?? null) as Record<string, any> | null;
    },
    enabled: interactionReady,
    staleTime: 5 * 60_000,
  });
  const { data: wishlistCount } = useQuery({
    queryKey: ["wishlist-count", user?.id],
    queryFn: async () => {
      if (!user) return 0;
      const supabase = await getSupabase();
      const { count } = await supabase.from("wishlists").select("id", { count: "exact", head: true }).eq("user_id", user.id);
      return count || 0;
    },
    enabled: !!user,
    staleTime: 60_000,
  });
  const [desktopOpen, setDesktopOpen] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const phone = headerSettings?.["phone"] || "910-793-2894";
  const textPhone = headerSettings?.["text_phone"] || "18332405678";
  const address = headerSettings?.["address"] || "5625 Barbados Blvd, Castle Hayne, NC 28429";
  const showTextLink = headerSettings?.["show_text_link"] !== false;
  const accountPath = isSuperAdmin ? "/admin" : "/my-account";

  useEffect(() => {
    if (!desktopOpen) return;
    const close = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) setDesktopOpen(null);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setDesktopOpen(null);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", escape);
    };
  }, [desktopOpen]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeMobile = () => {
    setMobileOpen(false);
    setMobileExpanded(null);
  };

  return (
    <header className={cn("sticky top-0 z-50 border-b border-border transition-colors duration-300", scrolled ? "bg-background/85 shadow-sm backdrop-blur-md" : "bg-background")}>
      <AnnouncementBanner />
      <div className="border-b border-border bg-muted/60">
        <div className="container flex min-h-9 flex-wrap items-center justify-between gap-x-4 gap-y-1 py-1 text-xs text-secondary sm:text-sm">
          <a href="https://maps.google.com/?q=5625+Barbados+Blvd,+Castle+Hayne,+NC+28429" target="_blank" rel="noopener noreferrer" className="hidden items-center gap-1.5 hover:text-primary md:flex">
            <MapPin className="h-3.5 w-3.5" /> {address}
          </a>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <a href={`tel:${phone}`} className="flex items-center gap-1 font-semibold hover:text-primary"><Phone className="h-3.5 w-3.5" />{phone}</a>
            {showTextLink && <><span className="text-muted-foreground">or</span><a href={`sms:${textPhone}`} className="flex items-center gap-1 font-medium hover:text-primary"><MessageCircle className="h-3.5 w-3.5" />Text Us</a></>}
            <span className="text-muted-foreground">|</span>
            <Link to={user ? "/my-account/wishlist" : "/community-login?redirect=%2Fmy-account%2Fwishlist"} className="relative flex items-center gap-1 hover:text-primary" aria-label="Wishlist">
              <Heart className="h-3.5 w-3.5" /><span className="hidden sm:inline">Wishlist</span>
              {wishlistCount ? <span className="rounded-full bg-primary px-1.5 text-[10px] font-bold leading-4 text-primary-foreground">{wishlistCount > 9 ? "9+" : wishlistCount}</span> : null}
            </Link>
            {user ? (
              <>
                <Suspense fallback={null}><NotificationDropdown userId={user.id} /></Suspense>
                <Link to={accountPath} className="flex items-center gap-1 hover:text-primary"><User className="h-3.5 w-3.5" />My Account</Link>
                <Button type="button" variant="ghost" size="sm" onClick={signOut} className="h-auto gap-1 p-0 text-xs font-normal text-secondary hover:bg-transparent hover:text-primary sm:text-sm"><LogOut className="h-3.5 w-3.5" />Logout</Button>
              </>
            ) : (
              <Link to="/community-login" className="flex items-center gap-1 hover:text-primary"><LogIn className="h-3.5 w-3.5" />Login / Sign Up</Link>
            )}
            <Button type="button" variant="ghost" size="sm" onClick={() => hardRefresh()} className="h-auto gap-1 p-0 text-xs font-normal text-secondary hover:bg-transparent hover:text-primary sm:text-sm" aria-label="Force refresh"><RefreshCw className="h-3.5 w-3.5" /><span className="hidden sm:inline">Refresh</span></Button>
          </div>
        </div>
      </div>
      <div className={cn("container flex h-16 items-center gap-6 md:h-[72px] lg:gap-10 lg:transition-all lg:duration-300", scrolled ? "lg:h-14" : "lg:h-[72px]")}>
        <Link to="/" className="flex shrink-0 items-center" aria-label="Rex & Sons RVs home">
          <img src={STATIC_ASSETS.logo400} {...(homepage ? {} : { srcSet: CARMAX_LOGO_SRCSET, sizes: CARMAX_LOGO_SIZES })} alt="Rex & Sons RVs" width={240} height={72} className={cn("h-12 w-auto md:h-16 lg:transition-all lg:duration-300", scrolled ? "lg:h-12" : "lg:h-16")} />
        </Link>

        <nav ref={navRef} className="hidden items-center gap-1 lg:flex" aria-label="Main menu">
          {MENU.map((item) => {
            const open = desktopOpen === item.label;
            return (
              <div key={item.label} className="relative">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setDesktopOpen(open ? null : item.label)}
                  aria-expanded={open}
                  className={cn(
                    "h-10 gap-1 text-base font-semibold text-secondary hover:bg-transparent hover:text-primary",
                    item.pill ? "ml-2 rounded-full border border-secondary/30 px-5 hover:border-primary" : "rounded-full px-4",
                    open && (item.pill ? "border-primary text-primary" : "text-primary"),
                  )}
                >
                  {item.label}
                  {item.pill && <ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} />}
                </Button>
                {open && (
                  <div className="absolute left-0 top-full z-50 mt-3 w-72 rounded-2xl border border-border bg-popover p-2 shadow-2xl">
                    {item.children.map((child) => (
                      <div key={`${item.label}-${child.label}`}>
                        {child.divider && <div className="mx-3 my-2 border-t border-border" />}
                        <Link
                          to={child.href}
                          onClick={() => setDesktopOpen(null)}
                          className="block rounded-xl px-4 py-2.5 text-[15px] font-medium text-secondary transition-colors hover:bg-muted hover:text-primary"
                        >
                          {child.label}
                        </Link>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="ml-auto flex shrink-0 items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="rounded-full text-secondary lg:hidden"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((open) => !open)}
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {mobileOpen && (
        <div className="max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-border bg-background lg:hidden">
          <nav className="container flex flex-col py-2" aria-label="Mobile menu">
            {MENU.map((item) => (
              <div key={item.label} className="border-b border-border">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setMobileExpanded((open) => (open === item.label ? null : item.label))}
                  aria-expanded={mobileExpanded === item.label}
                  className="h-12 w-full justify-between rounded-none px-0 text-[17px] font-semibold text-secondary hover:bg-transparent hover:text-primary"
                >
                  {item.label}
                  <ChevronDown className={cn("h-5 w-5 transition-transform", mobileExpanded === item.label && "rotate-180")} />
                </Button>
                {mobileExpanded === item.label && (
                  <div className="pb-2">
                    {item.children.map((child) => (
                      <Link
                        key={`${item.label}-${child.label}`}
                        to={child.href}
                        onClick={closeMobile}
                        className={cn("block rounded-xl py-3 pl-4 pr-3 text-base font-medium text-secondary hover:bg-muted hover:text-primary", child.divider && "mt-2 border-t border-border")}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <a href={`tel:${phone}`} className="mt-3 flex items-center justify-center gap-2 rounded-full bg-primary py-3 font-semibold text-primary-foreground">
              <Phone className="h-4 w-4" /> Call {phone}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
